import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDeactivateUser } from '../../hooks/useUsers';
import type { DeactivateUserBody } from '../../types/user';

interface DeactivateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName?: string;
}

export function DeactivateUserDialog({
  open,
  onOpenChange,
  userId,
  userName,
}: DeactivateUserDialogProps) {
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [rehire, setRehire] = useState<'' | 'Yes' | 'No'>('');
  const deactivate = useDeactivateUser();

  useEffect(() => {
    if (open) {
      setEndDate(new Date().toISOString().slice(0, 10));
      setReason('');
      setRehire('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!endDate) return;
    const data: DeactivateUserBody = { endDate };
    if (reason) data.reasonForLeaving = reason;
    if (rehire) data.wouldYouReEmploy = rehire;
    await deactivate.mutateAsync({ userId, data });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Deactivate User
          </DialogTitle>
          <DialogDescription>
            {userName
              ? `${userName} will lose access immediately.`
              : 'This user will lose access immediately.'}{' '}
            Their record is kept for audit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="end_date">
              Last Day of Employment <span className="text-red-600">*</span>
            </Label>
            <Input
              id="end_date"
              type="date"
              value={endDate}
              required
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Leaving</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Short note for HR records"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="rehire">Would you re-employ?</Label>
            <Select value={rehire} onValueChange={(v: 'Yes' | 'No') => setRehire(v)}>
              <SelectTrigger id="rehire">
                <SelectValue placeholder="Select answer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deactivate.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!endDate || deactivate.isPending}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {deactivate.isPending ? 'Deactivating…' : 'Deactivate User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
